import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, Building2, ShieldCheck, Inbox, UserCheck, Sparkles, TrendingUp,
} from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { useAdminDashboard } from '@/lib/admin';

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const data = useAdminDashboard();
  const d = data.data;

  const tiles = [
    { Icon: Users,       label: t('admin.dash.totalUsers'),       value: d?.totalUsers ?? 0,            accent: 'from-indigo-500 to-fuchsia-500' },
    { Icon: UserCheck,   label: t('admin.dash.verifiedUsers'),    value: d?.verifiedUsers ?? 0,         accent: 'from-emerald-500 to-sky-500' },
    { Icon: Sparkles,    label: t('admin.dash.staffUsers'),       value: d?.staffUsers ?? 0,            accent: 'from-fuchsia-500 to-amber-500' },
    { Icon: TrendingUp,  label: t('admin.dash.signupsLast7d'),    value: d?.signupsLast7d ?? 0,         accent: 'from-sky-500 to-indigo-500' },
    { Icon: Building2,   label: t('admin.dash.totalCompanies'),   value: d?.totalCompanies ?? 0,        accent: 'from-violet-500 to-rose-500' },
    { Icon: ShieldCheck, label: t('admin.dash.verifiedCompanies'),value: d?.verifiedCompanies ?? 0,     accent: 'from-emerald-500 to-teal-500' },
    { Icon: ShieldCheck, label: t('admin.dash.pendingVerifs'),    value: d?.pendingVerifications ?? 0,  accent: 'from-amber-500 to-rose-500' },
    { Icon: Inbox,       label: t('admin.dash.openTickets'),      value: d?.openTickets ?? 0,           accent: 'from-rose-500 to-fuchsia-500' },
  ];

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('admin.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('admin.dash.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.dash.subtitle')}</p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {tiles.map((tile, i) => (
            <motion.div
              key={tile.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-soft"
            >
              <div className={`pointer-events-none absolute -top-12 -end-12 h-32 w-32 rounded-full bg-gradient-to-br ${tile.accent} opacity-10 blur-2xl`} />
              <div className="relative flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{tile.label}</p>
                  <p className="num mt-2 text-3xl font-bold tracking-tight">{tile.value}</p>
                </div>
                <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${tile.accent} text-white shadow-glow`}>
                  <tile.Icon className="h-4 w-4" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/users"     className="QuickLink"><QuickLink icon={Users}       title={t('admin.dash.manageUsers')}     body={t('admin.dash.manageUsersBody')} /></Link>
          <Link to="/admin/companies" className="QuickLink"><QuickLink icon={Building2}   title={t('admin.dash.manageCompanies')} body={t('admin.dash.manageCompaniesBody')} /></Link>
          <Link to="/admin/inbox"     className="QuickLink"><QuickLink icon={Inbox}       title={t('admin.dash.openInbox')}       body={t('admin.dash.openInboxBody')} /></Link>
        </div>
      </div>
    </AppShell>
  );
}

function QuickLink({ icon: Icon, title, body }: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }) {
  return (
    <div className="group rounded-2xl border border-border bg-card p-5 shadow-soft hover:shadow-card-lift hover:border-primary/40 transition">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition">
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
